/**
 * WHAT A PRESS ON THE PHOTO MEANS FOR A SUGGESTED RUN.
 *
 * Pulled out of the pointer handler so the rule can be stated once and tested
 * without a canvas, a renderer or a browser. StageView owns the gesture; this
 * owns the decision.
 *
 * Nothing here mutates anything or prices anything. A proposal is not a
 * Strand; this only says which run the finger landed on.
 */

import type { RoofProposal } from '../types/proposal'
import { distanceToPath, toPx, type PxPoint } from './geometry'

/**
 * Screen-space targets, divided by zoom at the call site so they stay the same
 * size under the finger whether the photo is fitted or magnified.
 *
 * A circle gets a larger target than the line it sits on, because the circle
 * is the more specific intent: a press near a vertex means "move this end",
 * and the body drag is the fallback.
 */
export const GRAB_VERTEX_PX = 22
export const GRAB_BODY_PX = 18

export type SelectedGrab = { kind: 'vertex'; index: number } | { kind: 'body' }

/** A run still waiting to be included, and therefore drawn with grabbable circles. */
const isWaiting = (p: RoofProposal) => p.status === 'proposed' && p.points.length >= 2

/**
 * WHAT A PRESS DOES TO A WAITING RUN.
 *
 * Vertices first, then the body. A confirmed run is not handled here: it owns
 * a Strand, the Strand is what is drawn as lights, and StageView reaches it
 * through the layer hit test so that moving it withdraws the confirmation on
 * the first frame.
 *
 * There is no mode to enter first. The run is drawn with a circle on every
 * vertex, and a circle on a line means "grab me" in every tool anyone has
 * used, so the picture on the screen has to be telling the truth. It was not:
 * the circles were live only after finding "Edit on the photo" in a side
 * panel, and a press on one did nothing at all.
 *
 * Returns null when the press missed, which is what lets a press on empty
 * photo go on to deselect or draw.
 */
export function selectedProposalGrab(
  p: RoofProposal | undefined,
  at: PxPoint,
  w: number,
  h: number,
  zoom: number,
): SelectedGrab | null {
  if (!p || !isWaiting(p)) return null

  const vertex = GRAB_VERTEX_PX / zoom
  for (let i = 0; i < p.points.length; i++) {
    const c = toPx(p.points[i], w, h)
    if (Math.hypot(c.x - at.x, c.y - at.y) <= vertex) return { kind: 'vertex', index: i }
  }

  const body = GRAB_BODY_PX / zoom
  const d = distanceToPath(
    p.points.map((q) => toPx(q, w, h)),
    at,
  )
  return d <= body ? { kind: 'body' } : null
}

/**
 * WHICH WAITING RUN A PRESS LANDED ON.
 *
 * Topmost wins, so a run added later sits over an earlier one - the same order
 * they are drawn in. The press that finds one only SELECTS it: it does not
 * move it, because a press that both selected and moved would make a gable's
 * second run impossible to draw, its first corner sitting exactly on the end
 * of the run before it.
 */
export function proposalPick(
  proposals: RoofProposal[],
  at: PxPoint,
  w: number,
  h: number,
  zoom: number,
): string | null {
  const body = GRAB_BODY_PX / zoom
  for (let i = proposals.length - 1; i >= 0; i--) {
    const p = proposals[i]
    if (!isWaiting(p)) continue
    const d = distanceToPath(
      p.points.map((q) => toPx(q, w, h)),
      at,
    )
    if (d <= body) return p.id
  }
  return null
}
